import _extends from "@babel/runtime/helpers/extends";
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { XIcon } from '@pattern-library/icon';
import { FormLabelPopover } from '@pattern-library/message-popover';
import { FormInputErrors, FormInputLabel } from '@pattern-library/form-input';
const propTypes = {
  /**
   * DOM ID for the input element
   */
  id: PropTypes.string.isRequired,
  /**
   * Text label shown above the input element
   */
  label: PropTypes.string.isRequired,
  /**
   * Accessible label for clear button (not visually rendered)
   */
  clearButtonLabel: PropTypes.string,
  /**
   * The severity of the input's error state
   */
  errorLevel: PropTypes.oneOf(['error', 'warning']),
  /**
   * Error message(s) to display when `hasError` is enabled
   */
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  /**
   * Comma-separated list of one or more file types, or
   * [unique file type specifiers](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/accept#unique_file_type_specifiers),
   * describing which file types to allow
   */
  fileTypes: PropTypes.string,
  /**
   * Flag to enable the input's error state
   */
  hasError: PropTypes.bool,
  /**
   * Flag to accessibly hide the input label
   */
  hideLabel: PropTypes.bool,
  /**
   * Content for the info popover. If not provided, the popover will not be rendered.
   * If useEmbeddedLabel is true, the popover will not be rendered.
   */
  infoPopoverContent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * Additional props to pass to the message popover component
   */
  infoPopoverProps: PropTypes.object,
  /**
   * Additional classes for the input element
   */
  inputClass: PropTypes.string,
  /**
   * Flag to enable the input's disabled state
   */
  isDisabled: PropTypes.bool,
  /**
   * Indicates whether the input takes multiple files
   */
  isMultiple: PropTypes.bool,
  /**
   * Flag to indicate whether this input is a required field.
   */
  isRequired: PropTypes.bool,
  /**
   * Appended to the text label.
   * Defaults to (optional) for non-required inputs, but can be overridden.
   */
  optionalInputLabel: PropTypes.string,
  /**
   * Append asterisk to label
   */
  showRequiredAsterisk: PropTypes.bool,
  /**
   * Text message shown below the input
   */
  subtext: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
};
const defaultProps = {
  clearButtonLabel: 'Remove selected file',
  hasError: false,
  hideLabel: false,
  errorMessage: null,
  errorLevel: 'error',
  fileTypes: '',
  infoPopoverContent: null,
  infoPopoverProps: {},
  inputClass: '',
  isDisabled: false,
  isMultiple: false,
  isRequired: false,
  optionalInputLabel: '(optional)',
  showRequiredAsterisk: false
};
function FileInput(props) {
  const {
    clearButtonLabel,
    errorLevel,
    errorMessage,
    fileTypes,
    hasError,
    hideLabel,
    id,
    infoPopoverContent,
    infoPopoverProps,
    inputClass,
    isDisabled,
    isMultiple,
    isRequired,
    label,
    optionalInputLabel,
    showRequiredAsterisk,
    subtext,
    ...inputProps
  } = props;
  const [hasFile, setHasFile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef();
  const hasInfoPopover = infoPopoverContent !== null;
  const ids = {
    help: [`${id}-help`, hasError && Boolean(errorMessage)],
    subtext: [`${id}-subtext`, Boolean(subtext)],
    popover: [`${id}-popover`, hasInfoPopover]
  };
  const onChange = () => {
    if (inputRef.current) {
      setHasFile(inputRef.current.files.length > 0);
    }
  };
  const onClear = () => {
    if (inputRef.current && !isDisabled) {
      inputRef.current.value = '';
      setHasFile(false);
      inputRef.current.dispatchEvent(new Event('change', {
        bubbles: true
      }));
    }
  };
  const onDragEnter = e => {
    e.stopPropagation();
    setIsDragging(true);
  };
  const onDragOver = e => {
    e.stopPropagation();
  };
  const onDragLeave = e => {
    e.stopPropagation();
    setIsDragging(false);
  };
  const onDrop = e => {
    onDragLeave(e);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classnames('asc-form-group', 'asc-file-input-group', {
      'is-disabled': isDisabled,
      [`asc-form-input--${errorLevel}`]: hasError
    })
  }, /*#__PURE__*/React.createElement(FormInputLabel, {
    hideLabel: hideLabel,
    inputId: id,
    isRequired: isRequired,
    label: label,
    optionalInputLabel: optionalInputLabel,
    showRequiredAsterisk: showRequiredAsterisk
  }), hasInfoPopover && /*#__PURE__*/React.createElement(FormLabelPopover, {
    messagePopoverId: ids.popover[0],
    messagePopoverContent: infoPopoverContent,
    messagePopoverProps: infoPopoverProps
  }), /*#__PURE__*/React.createElement("div", {
    className: classnames('asc-file-input', inputClass, {
      'asc-file-input--has-file': hasFile,
      'asc-file-input--dragover': isDragging
    })
  }, /*#__PURE__*/React.createElement("input", _extends({
    type: "file",
    id: id,
    onChange: onChange,
    onDragEnter: onDragEnter,
    onDragLeave: onDragLeave,
    onDragOver: onDragOver,
    onDrop: onDrop,
    disabled: isDisabled,
    accept: fileTypes,
    multiple: isMultiple,
    ref: inputRef
  }, inputProps)), hasFile && /*#__PURE__*/React.createElement("button", {
    className: "asc-file-input_clear-btn",
    type: "button",
    onClick: onClear
  }, /*#__PURE__*/React.createElement(XIcon, {
    size: "xxs",
    color: "gray-60"
  }), /*#__PURE__*/React.createElement("span", {
    className: "asc-acc-hidden"
  }, clearButtonLabel))), /*#__PURE__*/React.createElement(FormInputErrors, {
    containerId: ids.help[0],
    errorLevel: errorLevel,
    errorMessage: errorMessage,
    hasError: hasError
  }), subtext && /*#__PURE__*/React.createElement("p", {
    id: ids.subtext[0],
    className: "asc-form-subtext"
  }, subtext));
}
FileInput.propTypes = propTypes;
FileInput.defaultProps = defaultProps;
export default FileInput;